import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { useClinic } from "@/store/ClinicContext";
import { formatBRL } from "@/data/mockData";

const EMPTY = { name: "", valorConsulta: 100, prazoDias: 30, glosaPercent: 5 };

export const liquidoRepasse = (p) =>
  (Number(p.valorConsulta) || 0) * (1 - (Number(p.glosaPercent) || 0) / 100);

export const HealthPlansAdmin = () => {
  const { healthPlans, addHealthPlan, updateHealthPlan, removeHealthPlan } = useClinic();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k, v) => set(k, v === "" ? "" : Number(v));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const save = () => {
    if (!form.name.trim()) return toast.error("Informe o nome do convênio.");
    if (editing) { updateHealthPlan(editing.id, form); toast.success("Convênio atualizado!", { description: form.name }); }
    else { addHealthPlan(form); toast.success("Convênio cadastrado!", { description: form.name }); }
    setOpen(false);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <div>
          <h2 className="font-heading text-base font-bold text-slate-900">Convênios & Planos de Saúde</h2>
          <p className="text-xs text-slate-500">Defina o valor pago por consulta em cada convênio, o prazo de pagamento e a glosa estimada.</p>
        </div>
        <button data-testid="new-plan-btn" onClick={openNew} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700">
          <Plus className="h-3.5 w-3.5" /> Novo Convênio
        </button>
      </div>

      <div className="divide-y divide-slate-100">
        {healthPlans.map((p, i) => (
          <motion.div key={p.id} data-testid={`plan-row-${p.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} className="flex items-center gap-3 px-6 py-3.5 hover:bg-slate-50/80">
            <div className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 sm:flex"><ShieldCheck className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-900">{p.name}</p>
              <p className="text-xs text-slate-500">
                Valor pago pela consulta {formatBRL(p.valorConsulta)}
                <span className="hidden sm:inline"> · prazo {p.prazoDias}d · glosa {p.glosaPercent}%</span>
              </p>
              <p className="mt-0.5 text-xs text-emerald-600 sm:hidden">Líquido: <span className="font-mono-num font-bold" data-testid={`plan-liquido-m-${p.id}`}>{formatBRL(liquidoRepasse(p))}</span></p>
            </div>
            <div className="hidden text-right sm:block">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Líquido por consulta</p>
              <p className="font-mono-num text-sm font-bold text-emerald-600" data-testid={`plan-liquido-${p.id}`}>{formatBRL(liquidoRepasse(p))}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button data-testid={`edit-plan-${p.id}`} onClick={() => openEdit(p)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><Pencil className="h-3.5 w-3.5" /></button>
              <button data-testid={`delete-plan-${p.id}`} onClick={() => { removeHealthPlan(p.id); toast("Convênio removido", { description: p.name }); }} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </motion.div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="plan-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Convênio" : "Novo Convênio"}</DialogTitle>
            <DialogDescription>Informe o valor que o convênio paga por consulta. O valor é editável e pode ser atualizado quando houver reajuste.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs font-semibold text-slate-600">Nome do convênio</Label><Input data-testid="plan-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="Ex.: Amil, Hapvida, Porto Seguro..." /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Valor pago por consulta (R$)</Label><Input data-testid="plan-valor-input" type="number" value={form.valorConsulta} onChange={(e) => num("valorConsulta", e.target.value)} className="mt-1.5" placeholder="Ex.: 90" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-slate-600">Prazo de pagamento (dias)</Label><Input data-testid="plan-prazo-input" type="number" value={form.prazoDias} onChange={(e) => num("prazoDias", e.target.value)} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold text-slate-600">Glosa estimada (%)</Label><Input data-testid="plan-glosa-input" type="number" value={form.glosaPercent} onChange={(e) => num("glosaPercent", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-700">
              Líquido estimado por consulta (após glosa): <b className="font-mono-num">{formatBRL(liquidoRepasse(form))}</b>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button data-testid="plan-save-btn" onClick={save} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">{editing ? "Salvar" : "Cadastrar"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
